import { Dangerous } from '@mui/icons-material';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import CustomCheckbox from '../../components/custom/controls/CustomCheckbox';
import CustomSwitch from '../../components/custom/controls/CustomSwitch';
import { toggleInlineRowSx } from '../../components/custom/controls/customToggleStyles';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import CustomButton from '../../components/custom/btn/CustomButton';
import CustomBetInputDialog from '../../components/custom/dialog/CustomBetInputDialog';
import {
	showErrorSnackbar,
	showSuccessSnackbar,
} from '../../components/custom/snackbar/snackbarSlice';
import useFetchCurrentUser from '../../components/hooks/useFetchCurrentUser';
import {
	isValidBetOddsInput,
	isValidBetSizeInput,
	isAllowedIntegerInput,
} from '../../components/utils/decimalInput';
import { getFullBetTitle } from '../../components/utils/stringTransform';
import CalendarNode from '../admin/calendars/CalendarNode';
import { getAllSeasonCalendarNodes } from '../admin/calendars/calendarsSlice';
import { selectAllCalendarNodes } from '../admin/calendars/selectors';
import Calendar from '../admin/calendars/types/Calendar';
import League from '../admin/leagues/types/League';
import { getActiveSeason } from '../admin/seasons/seasonsSlice';
import { selectActiveSeason } from '../admin/seasons/selectors';
import Team from '../admin/teams/types/Team';
import SimpleUser from '../auth/types/SimpleUser';
import {
	FALLBACK_DEFAULT_BET_SIZE,
	findLeagueMatchdayInCalendars,
	resolveBetSizeForBetInput,
	resolveSeasonDefaultBetSize,
} from './betSizeDefaults';
import BetInputLeague from './BetInputLeague';
import BetInputOdds from './BetInputOdds';
import BetInputPlayer from './BetInputPlayer';
import BetInputTeams from './BetInputTeams';
import BetInputTitle from './BetInputTitle';
import BetSummaryInfo from './BetSummaryInfo';
import MatchDayForm from './MatchDayForm';
import { resolveDefaultMatchDay } from '../../components/utils/matchdaySlots';
import { addEmptyBet } from './betsSlice';
import {
	betInputPageRootSx,
	betInputSelectedBetSx,
	betInputTextFieldSx,
} from './betInputPageStyles';
import BetTitle from './types/BetTitle';

export default function BetInputContainer(): JSX.Element {
	const dispatch = useAppDispatch();
	const season = useAppSelector(selectActiveSeason);
	const calendarNodes = useAppSelector(selectAllCalendarNodes);
	const [calendar, setCalendar] = useState<Calendar | undefined>();
	const [selectedUser, setSelectedUser] = useState<SimpleUser | undefined>(undefined);
	const [selectedLeague, setSelectedLeague] = useState<League>();
	const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
	const [matchDay, setMatchDay] = useState<string>('1');
	const [selectedHomeTeam, setSelectedHomeTeam] = useState<Team | undefined>(undefined);
	const [selectedAwayTeam, setSelectedAwayTeam] = useState<Team | undefined>(undefined);
	const [selectedEmptyBetSize, setSelectedEmptyBetSize] = useState<string>(
		String(resolveSeasonDefaultBetSize(season ?? undefined))
	);
	const [resetTeams, setResetTeams] = useState(false);
	const [isEmptyBet, setIsEmptyBet] = useState(false);
	const [selectedBetTitle, setSelectedBetTitle] = useState<BetTitle>();
	const [selectedBetOdds, setSelectedBetOdds] = useState<string>('');
	const [selectedBetSize, setSelectedBetSize] = useState<string>('');
	const [showSendButton, setShowSendButton] = useState<boolean>(false);
	const [openDialog, setOpenDialog] = useState(false);
	const [openDialogEmptyBet, setOpenDialogEmptyBet] = useState(false);
	const [openDialogTwoEmptyBet, setOpenDialogTwoEmptyBet] = useState(false);

	useFetchCurrentUser();

	const scrollToBottom = (): void => {
		window.scrollTo({ top: 100, behavior: 'smooth' });
	};

	// добавление ставки
	const handleSaveClick = useCallback(async () => {
		setOpenDialog(false);
		if (season && selectedUser && selectedHomeTeam && selectedAwayTeam) {
			if (!isValidBetOddsInput(selectedBetOdds) || !isValidBetSizeInput(selectedBetSize)) {
				dispatch(showErrorSnackbar({ message: 'betCoefIsNotNumber' }));
				return;
			}
			// Opened bets require match_schedules._id (player flow: OddsPick / Results).
			// Moderator calendar UI is not wired to schedules yet — fail loudly, no resolve fallback.
			dispatch(showErrorSnackbar({ message: 'matchScheduleNotFound' }));
		}
	}, [season, selectedUser, selectedHomeTeam, selectedAwayTeam, selectedBetOdds, selectedBetSize, dispatch]);

	// добавление пустой ставки
	const handleSaveEmptyBet = useCallback(async () => {
		if (season && selectedUser) {
			const dispatchResult = await dispatch(
				addEmptyBet({
					newEmptyBet: {
						seasonId: season.id,
						leagueId: selectedLeagueId,
						userId: selectedUser.id,
						matchDay,
						betSize: Number(selectedEmptyBetSize),
						calendarNodeId: calendar?.id,
					},
				})
			);

			if (addEmptyBet.fulfilled.match(dispatchResult)) {
				if (openDialogEmptyBet) {
					dispatch(showSuccessSnackbar({ message: t('emptyBetWasSuccessfullyAdded') }));
				} else {
					dispatch(showSuccessSnackbar({ message: t('twoEmptyBetsWasSuccessfullyAdded') }));
				}
			}
			if (addEmptyBet.rejected.match(dispatchResult)) {
				dispatch(showErrorSnackbar({ message: dispatchResult.error.message }));
			}
			setOpenDialogEmptyBet(false);
			setOpenDialogTwoEmptyBet(false);
		}
	}, [
		openDialogEmptyBet,
		season,
		selectedEmptyBetSize,
		selectedLeagueId,
		matchDay,
		selectedUser,
		calendar,
	]);

	// хэндлеры
	const handleEmptyBet = (event: React.ChangeEvent<HTMLInputElement>): void => {
		setIsEmptyBet(event.target.checked);
	};

	const handleUserSelection = (player: SimpleUser): void => {
		setSelectedUser(player);
	};

	const handleLeagueSelection = (league: League): void => {
		setSelectedLeague(league);
		setSelectedLeagueId(league.id);
		setMatchDay(resolveDefaultMatchDay(league.currentMatchDay, league.matchdaySlots));
		setSelectedHomeTeam(undefined);
		setSelectedAwayTeam(undefined);
		setSelectedBetTitle(undefined);
		setShowSendButton(false);
	};

	const handleMatchDay = (title: string): void => {
		setMatchDay(title);
	};

	const handleHomeTeamSelection = (homeTeam: Team): void => {
		setSelectedHomeTeam(homeTeam);
	};

	const handleAwayTeamSelection = (awayTeam: Team): void => {
		setSelectedAwayTeam(awayTeam);
		requestAnimationFrame(() => {
			const active = document.activeElement;
			if (active instanceof HTMLElement) {
				active.blur();
			}
		});
	};

	const handleBetCancel = (): void => {
		setSelectedBetTitle(undefined);
		setSelectedBetOdds('');
		setShowSendButton(false);
	};

	const handleBetTitleSelection = (code: number, label: string): void => {
		setSelectedBetTitle({ code, label, isNot: selectedBetTitle?.isNot ?? false });
		setShowSendButton(true);
		scrollToBottom();
	};

	const handleOddsSelection = (betOdds: string, betSize: string): void => {
		setSelectedBetOdds(betOdds);
		setSelectedBetSize(betSize);
	};

	const handleOpenDialog = (): void => {
		setOpenDialog(true);
	};

	const handleEmptyBetSize = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const size = event.target.value;
		if (!isAllowedIntegerInput(size)) {
			return;
		}
		setSelectedEmptyBetSize(size);
	};

	const handleOpenDialogEmptyBet = (): void => {
		setOpenDialogEmptyBet(true);
	};

	const handleSaveTwoEmptyBet = async (): Promise<void> => {
		await handleSaveEmptyBet();
		handleSaveEmptyBet();
	};

	const handleOpenDialogTwoEmptyBet = (): void => {
		setOpenDialogTwoEmptyBet(true);
	};

	const handleIsNotChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const { checked } = event.target;
		if (selectedBetTitle) {
			setSelectedBetTitle({ ...selectedBetTitle, isNot: checked });
		}
	};

	const handleCloseDialog = (): void => {
		setOpenDialog(false);
		setOpenDialogEmptyBet(false);
		setOpenDialogTwoEmptyBet(false);
	};

	const calendarMatch = useMemo(
		() =>
			findLeagueMatchdayInCalendars(
				calendarNodes,
				selectedLeague?.leagueCode,
				matchDay,
				selectedLeague?.id
			),
		[calendarNodes, selectedLeague?.leagueCode, selectedLeague?.id, matchDay]
	);

	useEffect(() => {
		setCalendar(calendarMatch?.calendar);
	}, [calendarMatch]);

	const resolvedDefaultBetSize = useMemo(() => {
		if (!season) {
			return String(FALLBACK_DEFAULT_BET_SIZE);
		}
		return String(
			resolveBetSizeForBetInput(
				resolveSeasonDefaultBetSize(season),
				matchDay,
				calendarMatch?.node
			)
		);
	}, [season, calendarMatch, matchDay]);

	useEffect(() => {
		setSelectedEmptyBetSize(resolvedDefaultBetSize);
		setSelectedBetSize(resolvedDefaultBetSize);
	}, [resolvedDefaultBetSize]);

	useEffect(() => {
		if (season?.id) {
			dispatch(getAllSeasonCalendarNodes(season.id));
		}
	}, [dispatch, season?.id]);

	useEffect(() => {
		if (!season) {
			dispatch(getActiveSeason());
		}
	}, []);

	return (
		<Box sx={betInputPageRootSx}>
			<Typography sx={{ textAlign: 'center', borderBottom: 2, pb: 1, mx: 2 }}>
				{t('addBets')}
			</Typography>
			<Box sx={[toggleInlineRowSx, { mx: 1, mt: 1 }] as SxProps<Theme>}>
				<Typography sx={{ fontWeight: '600' }}>{t('emptyBet')}?</Typography>
				<CustomSwitch
					checked={isEmptyBet}
					onChange={handleEmptyBet}
					inputProps={{ 'aria-label': 'controlled' }}
				/>
			</Box>
			<BetInputPlayer defaultValue={undefined} onUserSelect={handleUserSelection} />
			{selectedUser && (
				<Box>
					<BetInputLeague onLeagueSelect={handleLeagueSelection} />
					{selectedLeague && (
						<>
							<MatchDayForm
								key={selectedLeague.id}
								matchDay={matchDay}
								leagueCode={selectedLeague.leagueCode}
								matchdaySlots={selectedLeague.matchdaySlots}
								onMatchDay={handleMatchDay}
							/>
							{calendar ? <CalendarNode calendar={calendar} /> : <CalendarNode noCalendar />}
						</>
					)}
				</Box>
			)}
			{!isEmptyBet && (
				<>
					{selectedLeagueId && matchDay && (
						<BetInputTeams
							onHomeTeamSelect={handleHomeTeamSelection}
							onAwayTeamSelect={handleAwayTeamSelection}
							leagueId={selectedLeagueId}
							resetTeams={resetTeams}
						/>
					)}
					{selectedHomeTeam && selectedAwayTeam && !selectedBetTitle && (
						<BetInputTitle onBetTitleSelect={handleBetTitleSelection} />
					)}
					{selectedBetTitle && (
						<Box sx={{ my: 2, width: '18.2rem' }}>
							<Box sx={{ display: 'flex', justifyContent: 'center' }}>
								<Typography sx={betInputSelectedBetSx}>
									{getFullBetTitle(selectedBetTitle)}
								</Typography>
								<IconButton sx={{ p: 0 }} onClick={handleBetCancel}>
									<Dangerous
										color="error"
										sx={{ p: 0, mt: -0.75, ml: -0.5, mr: 0.5, fontSize: '3rem' }}
									/>
								</IconButton>
							</Box>
							<Box component="span" sx={toggleInlineRowSx}>
								<CustomCheckbox
									checked={selectedBetTitle.isNot}
									onChange={handleIsNotChange}
									inputProps={{ 'aria-label': 'controlled' }}
								/>
								<Typography component="span">{t('not')}</Typography>
							</Box>
						</Box>
					)}
					{selectedBetTitle && (
						<BetInputOdds
							defaultBetSize={selectedBetSize || resolvedDefaultBetSize}
							onOddsSelect={handleOddsSelection}
						/>
					)}
					{showSendButton &&
						isValidBetOddsInput(selectedBetOdds) &&
						isValidBetSizeInput(selectedBetSize) && (
						<CustomButton
							sx={{ mt: 3, height: '3rem', px: 6 }}
							onClick={handleOpenDialog}
							buttonText={t('sendBet')}
							buttonColor="secondary"
						/>
					)}
				</>
			)}
			{selectedLeagueId && isEmptyBet && (
				<>
					<Box
						sx={{
							mt: 2,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							textAlign: 'left',
						}}
					>
						<Typography sx={{ ml: 2, mr: 0.5, fontWeight: '600' }}> {t('amount')}</Typography>
						<Box component="form" autoComplete="off" sx={{ width: '3rem', pt: 0 }}>
							<TextField
								size="small"
								sx={betInputTextFieldSx as SxProps<Theme>}
								value={selectedEmptyBetSize}
								onChange={handleEmptyBetSize}
								inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
							/>
						</Box>
					</Box>
					<CustomButton
						sx={{ mt: 2, height: '2.5rem', px: 2.1, bgcolor: '#525252' }}
						onClick={handleOpenDialogEmptyBet}
						buttonText={t('sendEmptyBet')}
					/>
					<CustomButton
						sx={{ mt: 2, height: '2.5rem', px: 2.1, bgcolor: '#525252' }}
						onClick={handleOpenDialogTwoEmptyBet}
						buttonText={t('sendTwoEmptyBet')}
					/>
				</>
			)}

			<CustomBetInputDialog
				open={openDialog}
				onClose={handleCloseDialog}
				onSave={handleSaveClick}
				summaryComponent={
					<BetSummaryInfo
						message={t('addBet')}
						player={selectedUser}
						leagueCode={selectedLeague?.leagueCode || ''}
						matchDay={matchDay}
						homeTeam={selectedHomeTeam}
						awayTeam={selectedAwayTeam}
						betTitle={selectedBetTitle}
						betOdds={selectedBetOdds}
						betSize={selectedBetSize}
					/>
				}
			/>

			<CustomBetInputDialog
				open={openDialogEmptyBet}
				onClose={handleCloseDialog}
				onSave={handleSaveEmptyBet}
				title={t('addEmptyBet')}
			/>

			<CustomBetInputDialog
				open={openDialogTwoEmptyBet}
				onClose={handleCloseDialog}
				onSave={handleSaveTwoEmptyBet}
				title={t('addTwoEmptyBet')}
			/>
		</Box>
	);
}
